/**
 * Shim for `@react-navigation/stack`.
 *
 * Implements an in-memory stack navigator. Only the top scene is rendered
 * (screen state is held in Redux, so remounting on return is fine). Unknown
 * route names are delegated to a parent navigator, matching React Navigation.
 * The browser Back button maps to `goBack`.
 */
import React, {
  createElement,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  NavigationContext,
  RouteContext,
  FocusContext,
  type Navigation,
  type Route,
  type NavigationAction,
} from './native';
import { View } from '../web/react-native';

let keyCounter = 0;
const nextKey = (name: string) => `${name}-${++keyCounter}`;

interface ScreenConfig {
  name: string;
  component: React.ComponentType<any>;
  initialParams?: object;
  options?: any;
}

interface StackEntry {
  key: string;
  name: string;
  params?: object;
}

const ROUTE_URL_PARAM_KEYS = new Set([
  'snippetId',
  'rowIndex',
  'rollupIndex',
  'lastLink',
  'groupIndex',
  'isTeamLog',
  'previousScreen',
]);

const addParamToQuery = (query: URLSearchParams, key: string, value: unknown) => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    query.set(key, String(value));
    return;
  }

  if (Array.isArray(value)) {
    const allPrimitive = value.every(
      (entry) =>
        typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean',
    );
    if (allPrimitive) {
      query.set(key, value.map((entry) => String(entry)).join(','));
    }
  }
};

const buildRouteHash = (entry?: StackEntry, componentName?: string) => {
  if (!entry) return '#/unknown';

  const query = new URLSearchParams();
  const params = entry.params as Record<string, unknown> | undefined;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (!ROUTE_URL_PARAM_KEYS.has(key)) return;
      addParamToQuery(query, key, value);
    });
  }

  if (componentName) {
    query.set('component', componentName);
  }

  const serializedQuery = query.toString();
  return `#/${encodeURIComponent(entry.name)}${serializedQuery ? `?${serializedQuery}` : ''}`;
};

/** Marker component; configuration is read from its props by the Navigator. */
export function Screen(_props: {
  name: string;
  component: React.ComponentType<any>;
  initialParams?: object;
  options?: any;
  children?: any;
}): null {
  return null;
}

function readScreens(children: React.ReactNode): ScreenConfig[] {
  const screens: ScreenConfig[] = [];
  React.Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    // Support fragments wrapping <Screen> elements.
    if (child.type === React.Fragment) {
      screens.push(...readScreens((child.props as any).children));
      return;
    }
    const props = child.props as any;
    if (props && props.name && props.component) {
      screens.push({
        name: props.name,
        component: props.component,
        initialParams: props.initialParams,
        options: props.options,
      });
    }
  });
  return screens;
}

/** Per-scene wrapper that emits focus/blur to listeners registered on its navigation. */
function Scene({
  entry,
  component: Component,
  navigation,
  emitFocus,
}: {
  entry: StackEntry;
  component: React.ComponentType<any>;
  navigation: Navigation;
  emitFocus: (key: string) => void;
}) {
  const route: Route = { key: entry.key, name: entry.name, params: entry.params };

  useEffect(() => {
    emitFocus(entry.key);
    datadogRum.startView(entry.name); // report the active screen as a RUM view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.key]);

  return (
    <NavigationContext.Provider value={navigation}>
      <RouteContext.Provider value={route}>
        <FocusContext.Provider value={true}>
          <View style={{ flex: 1 }}>
            <Component navigation={navigation} route={route} />
          </View>
        </FocusContext.Provider>
      </RouteContext.Provider>
    </NavigationContext.Provider>
  );
}

function Navigator({
  children,
  initialRouteName,
  screenOptions,
}: {
  children?: React.ReactNode;
  initialRouteName?: string;
  screenOptions?: any;
}) {
  const parent = useContext(NavigationContext);
  const screens = useMemo(() => readScreens(children), [children]);
  const screenMap = useMemo(() => {
    const map: Record<string, ScreenConfig> = {};
    screens.forEach((s) => (map[s.name] = s));
    return map;
  }, [screens]);

  const firstName = initialRouteName ?? screens[0]?.name;
  const [stack, setStack] = useState<StackEntry[]>(() =>
    firstName ? [{ key: nextKey(firstName), name: firstName, params: screenMap[firstName]?.initialParams }] : [],
  );

  // Listeners registered per scene key via navigation.addListener.
  const listenersRef = useRef<Record<string, Record<string, Set<(e?: any) => void>>>>({});

  const emit = useCallback((key: string, type: string, payload?: any) => {
    listenersRef.current[key]?.[type]?.forEach((cb) => cb(payload));
  }, []);

  // When the set of screens changes (e.g. login → main flow), ensure the top
  // route still exists; otherwise reset to the new initial route.
  useEffect(() => {
    setStack((prev) => {
      if (prev.length && screenMap[prev[prev.length - 1].name]) return prev;
      return firstName ? [{ key: nextKey(firstName), name: firstName, params: screenMap[firstName]?.initialParams }] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenMap, firstName]);

  const navigationRef = useRef<Navigation>();

  const applyAction = useCallback(
    (action: NavigationAction) => {
      switch (action.type) {
        case 'NAVIGATE': {
          const { name, params } = action.payload;
          if (!screenMap[name]) {
            parent?.navigate(name, params);
            return;
          }
          setStack((prev) => {
            const existingIndex = prev.findIndex((e) => e.name === name);
            if (existingIndex >= 0) {
              // Pop back to the existing instance, updating its params.
              const next = prev.slice(0, existingIndex + 1);
              next[existingIndex] = { ...next[existingIndex], params: { ...next[existingIndex].params, ...params } };
              return next;
            }
            return [...prev, { key: nextKey(name), name, params }];
          });
          return;
        }
        case 'PUSH': {
          const { name, params } = action.payload;
          if (!screenMap[name]) {
            parent?.push(name, params);
            return;
          }
          setStack((prev) => [...prev, { key: nextKey(name), name, params }]);
          return;
        }
        case 'REPLACE': {
          const { name, params } = action.payload;
          if (!screenMap[name]) {
            parent?.replace(name, params);
            return;
          }
          setStack((prev) => [...prev.slice(0, -1), { key: nextKey(name), name, params }]);
          return;
        }
        case 'POP': {
          const count = action.payload?.count ?? 1;
          setStack((prev) => (prev.length > 1 ? prev.slice(0, Math.max(1, prev.length - count)) : prev));
          return;
        }
        case 'POP_TO_TOP':
          setStack((prev) => (prev.length > 1 ? [prev[0]] : prev));
          return;
        case 'GO_BACK':
          setStack((prev) => {
            if (prev.length > 1) return prev.slice(0, -1);
            parent?.goBack();
            return prev;
          });
          return;
        case 'RESET': {
          const routes: { name: string; params?: object }[] = action.payload.routes ?? [];
          setStack(routes.map((r) => ({ key: nextKey(r.name), name: r.name, params: r.params })));
          return;
        }
        case 'SET_PARAMS':
          setStack((prev) => {
            if (!prev.length) return prev;
            const next = [...prev];
            next[next.length - 1] = { ...next[next.length - 1], params: { ...next[next.length - 1].params, ...action.payload.params } };
            return next;
          });
          return;
        default:
          parent?.dispatch(action);
      }
    },
    [parent, screenMap],
  );

  // Build a stable navigation object.
  const navigation = useMemo<Navigation>(() => {
    const nav: Navigation = {
      navigate: (name, params) => applyAction({ type: 'NAVIGATE', payload: { name, params } }),
      push: (name, params) => applyAction({ type: 'PUSH', payload: { name, params } }),
      replace: (name, params) => applyAction({ type: 'REPLACE', payload: { name, params } }),
      goBack: () => applyAction({ type: 'GO_BACK' }),
      pop: (count) => applyAction({ type: 'POP', payload: { count } }),
      popToTop: () => applyAction({ type: 'POP_TO_TOP' }),
      reset: (state) => applyAction({ type: 'RESET', payload: state }),
      setParams: (params) => applyAction({ type: 'SET_PARAMS', payload: { params } }),
      setOptions: () => {},
      canGoBack: () => (navigationRef.current as any)?.__stackLength() > 1 || !!parent?.canGoBack(),
      dispatch: (action) => {
        const resolved = typeof action === 'function' ? action({ routes: [] }) : action;
        applyAction(resolved);
      },
      addListener: (type, callback) => {
        const key = (navigationRef.current as any)?.__currentKey();
        if (key) {
          (listenersRef.current[key] ??= {})[type] ??= new Set();
          listenersRef.current[key][type].add(callback);
        }
        return () => {
          if (key) listenersRef.current[key]?.[type]?.delete(callback);
        };
      },
      removeListener: (type, callback) => {
        const key = (navigationRef.current as any)?.__currentKey();
        if (key) listenersRef.current[key]?.[type]?.delete(callback);
      },
      isFocused: () => true,
      getParent: () => parent,
      getState: () => ({ index: 0, routes: [], type: 'stack' }),
    };
    return nav;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyAction, parent]);

  // Expose helpers used above without recreating the nav object each render.
  (navigation as any).__stackLength = () => stack.length;
  (navigation as any).__currentKey = () => stack[stack.length - 1]?.key;
  (navigation as any).getState = () => ({
    index: stack.length - 1,
    routes: stack.map((e) => ({ key: e.key, name: e.name, params: e.params })),
    type: 'stack',
    key: 'stack',
  });
  navigationRef.current = navigation;

  const top = stack[stack.length - 1];
  const config = top ? screenMap[top.name] : undefined;

  // Keep URL synced with the currently rendered in-memory route for easier debugging.
  useEffect(() => {
    if (!top) return;

    const componentName = config?.component?.displayName || config?.component?.name;
    const nextHash = buildRouteHash(top, componentName);
    if (window.location.hash === nextHash) return;

    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [top, config]);

  // Browser Back button → goBack when this navigator can handle it.
  useEffect(() => {
    const onPop = () => {
      if (stack.length > 1) {
        window.history.pushState(null, '', window.location.href);
        navigation.goBack();
      }
    };
    if (stack.length > 1) {
      window.history.pushState(null, '', window.location.href);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [stack.length, navigation]);
  if (!top) return null;
  if (!config) return null;

  return (
    <Scene key={top.key} entry={top} component={config.component} navigation={navigation} emitFocus={(k) => emit(k, 'focus')} />
  );
}

export function createStackNavigator() {
  return { Navigator, Screen, Group: ({ children }: { children?: React.ReactNode }) => createElement(React.Fragment, null, children) };
}

/* ------------------------- Transition stubs ------------------------------ */

export const TransitionPresets = {
  SlideFromRightIOS: {},
  ModalSlideFromBottomIOS: {},
  ModalPresentationIOS: {},
  FadeFromBottomAndroid: {},
  RevealFromBottomAndroid: {},
  ScaleFromCenterAndroid: {},
  DefaultTransition: {},
  ModalTransition: {},
};

export const CardStyleInterpolators = {
  forHorizontalIOS: () => ({}),
  forVerticalIOS: () => ({}),
  forModalPresentationIOS: () => ({}),
  forFadeFromBottomAndroid: () => ({}),
  forRevealFromBottomAndroid: () => ({}),
  forScaleFromCenterAndroid: () => ({}),
  forNoAnimation: () => ({}),
};

export const HeaderStyleInterpolators = {
  forUIKit: () => ({}),
  forFade: () => ({}),
  forStatic: () => ({}),
  forSlideLeft: () => ({}),
  forSlideRight: () => ({}),
  forSlideUp: () => ({}),
  forNoAnimation: () => ({}),
};

export const TransitionSpecs = {
  TransitionIOSSpec: {},
  FadeInFromBottomAndroidSpec: {},
  FadeOutToBottomAndroidSpec: {},
};

export const Header = () => null;
export const HeaderBackButton = () => null;

export default { createStackNavigator, TransitionPresets, CardStyleInterpolators, HeaderStyleInterpolators };
